'use client';

import { c as _c } from "react/compiler-runtime";
import { useTurnkeyModalActive } from '@morfi/backend-sdk-v2/client';
import { ExternalOverlay, Scroll, Sheet, type SheetViewProps } from '@silk-hq/components';
import { usePathname } from 'next/navigation';
import type { ComponentType, CSSProperties, ReactNode, RefObject } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Cancel01Icon } from '@/components/ui/icon/glyphs';
import { useKeyboardOpen, useVisualViewportCssVars } from '@/hooks/layout/use-visual-viewport-css-vars';
import { SILK_LICENSE } from '@/lib/silk';
import { DURATION_ENTRANCE_MS, DURATION_EXIT_MS, DURATION_POSITION_MS, EASE_DEFAULT_CSS, EASE_EXIT_CSS, EASE_POSITION_CSS } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// `useLayoutEffect` on the client (its cleanup runs in the commit/mutation
// phase, BEFORE Silk's passive-unmount cleanup — which is what lets
// `handlePresentedChange` tell an unmount apart from a real dismiss), but
// `useEffect` on the server to avoid the "useLayoutEffect does nothing on the
// server" warning. Picked once at module load, so it never changes per render.
const useIsomorphicLayoutEffect: typeof useLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

// Tailwind `sm` breakpoint = 40rem (default). Detached sheets switch from
// bottom-anchored (mobile) to centered (desktop) at this threshold.
const SM_BREAKPOINT_PX = 640;

/** SSR-safe matchMedia: false during SSR + first client render, then updates. */
function useIsDesktop() {
  const $ = _c(2);
  const [isDesktop, setIsDesktop] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const mq = window.matchMedia(`(min-width: ${String(SM_BREAKPOINT_PX)}px)`);
      setIsDesktop(mq.matches);
      const handler = function handler(e) {
        setIsDesktop(e.matches);
      };
      mq.addEventListener("change", handler);
      return () => {
        mq.removeEventListener("change", handler);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isDesktop;
}

// ---------------------------------------------------------------------------
// SilkSheet — Morfi wrapper for @silk-hq/components.
//
// This is the ONLY file in `frontend/src/` permitted to import from
// `@silk-hq/components` (enforced by precommit FQ-135). All license, detent
// length, inline-style and animation concerns live here so call sites stay
// FQ-002 / FQ-115 / FQ-120 / FQ-130 / FQ-132 / FQ-133 / FQ-134 clean.
//
// Variants:
//   - "side"     — anchored side sheet (replaces AppSheet). Drives the
//                  MobileMenu pilot (FR-013).
//   - "detached" — modal-style. Mobile bottom-sheet (content-driven height)
//                  via Silk's native contentPlacement="bottom". Desktop
//                  centered modal via contentPlacement="center" at sm+
//                  breakpoint (Silk contentPlacement is a runtime prop; we
//                  pick once at mount based on viewport width).
//   - "page"     — page-style for multi-step wizards (spec 098). Mobile:
//                  near-full-bleed (90svh) bottom-anchored sheet with the
//                  keyboard recipe always on (no opt-in needed). Desktop:
//                  identical to "detached" — same centered modal at sm+
//                  with the same `sm:max-w-*` cap per `size`. Used by
//                  create-proposal, send-gift, move-funds, transfer-funds,
//                  claim-gift-flow.
//
// Known limitations in this foundation drop (spec 095 Phase 1):
//   - Desktop centered-modal positioning for variant="detached" is the
//     simplest possible: Silk's "center" placement. Phase 3 batches may
//     add layout refinement (max-width caps mapped from `size` prop)
//     once real callsites exercise them.
//   - Reduced-motion handling relies on Silk's built-in
//     prefersReducedMotion default for animation `skip:` — verified by
//     reading dist/types.d.ts (every *AnimationSettings has
//     `{ skip: prefersReducedMotion }` as default).
// ---------------------------------------------------------------------------

export type SilkSheetVariant = 'detached' | 'page' | 'side';
export type SilkSheetSide = 'left' | 'right' | 'top' | 'bottom';
export type SilkSheetSize = 'sm' | 'default' | 'md' | 'lg' | 'xl';

// ── Internal: detents (intermediate snap positions) ──────────────────────
// Silk's "final detent" is implicit and equals "fully expanded inside the
// View". When we don't pass intermediate detents, the sheet sizes to its
// content's natural height (capped by the View's height — see
// `--silk-100-lvh-dvh-pct` on Sheet.View below). This avoids hardcoding
// arbitrary heights that clip taller content or leave whitespace below
// shorter content.
//
// Side T/B sheets still use an explicit detent because their "fully
// expanded" feels too vertical without one — a 60svh anchored slide-down
// is the conventional UX. L/R side sheets and detached modals are
// content-driven.
const SIDE_HEIGHT_DETENTS_TB: Record<SilkSheetSize, readonly string[]> = {
  sm: ['40svh'],
  default: ['60svh'],
  md: ['70svh'],
  lg: ['80svh'],
  xl: ['90svh']
} as const;
const DESKTOP_MAX_WIDTH: Record<SilkSheetSize, string> = {
  sm: 'sm:max-w-sm',
  default: 'sm:max-w-lg',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl'
};

// Side L/R width caps in CSS length form. Used to build the inline
// `width: min(100vw, <cap>)` rule on Sheet.Content for side L/R sheets:
//   • Phone / narrow viewports (100vw < cap): width = 100vw (full-screen).
//   • Tablet / desktop (100vw ≥ cap): width = cap.
// This mirrors the canonical Silk Sidebar example (Silk's own
// examples/css/src/components/Sidebar/Sidebar.css uses `width: min(90vw,
// 325px)` directly on Sheet.Content). Inline style is required because:
//   1. Tailwind v4's class extractor silently drops arbitrary values
//      containing commas, so `w-[min(100vw,28rem)]` never emits.
//   2. Splitting into `w-screen lg:max-w-*` loses to Silk's runtime
//      width measurement at the actual rendered viewport widths.
//   3. Silk's `--silk-aD` is overwritten inline by Silk's JS on mount
//      with the View's measured px dimensions — not a config knob.
// Inline style is the wrapper's existing FQ-002 exception boundary.
const SIDE_WIDTH_LR_CAP: Record<SilkSheetSize, string> = {
  sm: '24rem',
  default: '28rem',
  md: '28rem',
  lg: '32rem',
  xl: '36rem'
};

// ── Internal: variant + side → Silk contentPlacement / tracks ────────────

function placementForSide(side: SilkSheetSide): 'top' | 'bottom' | 'left' | 'right' {
  return side;
}

// Silk's dismiss-handler custom-event types aren't exported from the package
// barrel, so derive them from the callback form of the View props.
type SilkClickOutsideEvent = Parameters<Extract<NonNullable<SheetViewProps['onClickOutside']>, (...args: never[]) => unknown>>[0];
type SilkEscapeKeyDownEvent = Parameters<Extract<NonNullable<SheetViewProps['onEscapeKeyDown']>, (...args: never[]) => unknown>>[0];
type SilkDismissAutoFocusEvent = Parameters<Extract<NonNullable<SheetViewProps['onDismissAutoFocus']>, (...args: never[]) => unknown>>[0];

// ── Public props ──────────────────────────────────────────────────────────

export interface SilkSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once after a presented sheet finishes closing. Unlike
   *  `onOpenChange(false)`, this runs after Silk's exit transition, when it is
   *  safe for a caller to clear state that must remain visible while exiting. */
  onAfterClose?: (() => void) | undefined;
  /** Imperative openers without a Sheet.Trigger restore focus at Silk's
   * dismiss-autofocus lifecycle, after background inertness is released. */
  returnFocusRef?: RefObject<HTMLElement | null> | undefined;

  /** "detached" → modal/bottom-sheet (replaces AppDialog).
   *  "side"     → anchored side sheet (replaces AppSheet). */
  variant?: SilkSheetVariant | undefined;
  /** Required when variant='side'. Defaults to 'right'. Ignored for 'detached'. */
  side?: SilkSheetSide | undefined;
  /** Semantic size step. Maps internally to detents + desktop max-width caps. */
  size?: SilkSheetSize | undefined;
  /** Sheet surface tone. `secondary` (default) = page-canvas `--background-secondary`;
   *  `primary` = elevated card surface `--background-primary`. Applied as an inline
   *  `backgroundColor` because Silk's defaults layer beats Tailwind bg utilities. */
  surface?: 'primary' | 'secondary' | undefined;

  /** Title — always required for ARIA. Visible unless `header` slot is set
   *  or `srOnlyHeader` is true (then sr-only but still in the a11y tree). */
  title?: ReactNode | undefined;
  description?: ReactNode | undefined;
  /** Custom header — replaces default title/description rendering visually
   *  but `title` is still consumed as sr-only for ARIA. */
  header?: ReactNode | undefined;
  /** Render `title` / `description` as sr-only with no visible header chrome.
   *  Use for nav drawers where the sheet itself is the labeled affordance
   *  and a visible "Menu" heading is redundant. */
  srOnlyHeader?: boolean | undefined;
  footer?: ReactNode | undefined;
  children?: ReactNode;

  /** Optional declarative trigger element (mirrors AppDialog.trigger). */
  trigger?: ReactNode | undefined;

  /** Render the built-in × close button. Default true. */
  closeButton?: boolean | undefined;

  /** Passthrough className for the inner body wrapper (NOT Sheet.Content itself). */
  contentClassName?: string | undefined;
  footerClassName?: string | undefined;
  /** Passthrough className for `Sheet.Content` itself — the outermost visible modal
   *  box that owns the width cap, max-h-dvh cap, radius, and border. Use this for
   *  classes that affect the whole sheet's footprint (e.g. `sm:aspect-square`). */
  sheetClassName?: string | undefined;

  /** Opt into the on-screen-keyboard-friendly behaviour for surfaces that
   *  contain text / amount inputs. On mobile (detached variant only) this:
   *    • Adds `padding-bottom: var(--keyboard-offset, 0px)` to `Sheet.Content`.
   *      Silk pins `Sheet.Content`'s bottom edge at the layout-viewport bottom
   *      via JS-controlled scroll snap; with `height: auto` + `border-box`,
   *      the padding grows the content box upward — so the internal flex
   *      column of header / body / footer is pushed above the keyboard while
   *      the colored padding strip below the footer stays hidden behind it.
   *      `--keyboard-offset` is published live by `useVisualViewportCssVars`
   *      and updates on `visualViewport.resize`.
   *    • Dismisses the keyboard at the start of a swipe-to-dismiss via
   *      `onTravel` (focuses the View when progress drops below 0.999) so the
   *      keyboard collapses BEFORE the sheet animates out.
   *    • Removes swipe overshoot.
   *  Has no effect on desktop or on side variants.
   *
   *  Notes on rejected approaches:
   *    • Silk's full-height `SheetWithKeyboard` example hides the
   *      `<ModalFooter>` action button behind the keyboard.
   *    • Shrinking `Sheet.View` to `var(--vv-visible-height)` doesn't work
   *      because Silk's scroll-snap layout inside the View doesn't re-snap
   *      from an outer height CSS-var change.
   *    • `margin-bottom` on `Sheet.Content` doesn't work because Silk has
   *      already computed the JS-driven snap position; the margin is ignored.
   *    Only `padding-bottom` (which grows the content box from the inside
   *    while the snap-pinned bottom edge stays put) actually moves the chrome
   *    above the keyboard. */
  keyboard?: boolean | undefined;

  /** Opt out of Silk's swipe-to-dismiss gesture on desktop. Mobile is
   *  untouched (bottom-anchored sheets keep their swipe-down dismiss). Used
   *  by surfaces where accidental cursor-drag dismissal would discard
   *  meaningful in-progress state (e.g. the create-proposal wizard's draft).
   *  Esc and backdrop click still dismiss. Has no effect on mobile. */
  disableDesktopSwipe?: boolean | undefined;

  /** Render Silk's `<Sheet.Handle>` drag indicator centered at the top of the
   *  sheet — the small horizontal pill that signals "swipe me down to
   *  dismiss". Useful for bottom-anchored sheets without a visible header,
   *  where users otherwise have no affordance for the swipe gesture. */
  showHandle?: boolean | undefined;

  /** Suppress every built-in dismissal (Esc, backdrop click, swipe, and the ×
   *  button) and instead fire `onDismissAttempt`. Use to gate closing behind a
   *  confirmation while the sheet holds unsaved state (e.g. an in-flight AI
   *  generation).
   *
   *  Why this exists rather than refusing the close in `onOpenChange`: Silk is
   *  animation-driven — a dismiss gesture animates the sheet OUT and only then
   *  calls `onPresentedChange(false)`. Refusing by keeping `open` true leaves
   *  the sheet animated-out but still "presented", and Silk never re-presents
   *  it — the sheet is stuck closed and cannot be reopened without a reload.
   *  Suppressing the dismissal up-front keeps `presented` in sync. */
  guardDismiss?: boolean | undefined;
  /** Called when the user attempts to close while `guardDismiss` is true. */
  onDismissAttempt?: (() => void) | undefined;

  /** Keep the sheet presented through a client-side route change instead of
   *  dismissing it (see `useRouteChangeDismissal`). Only for app-shell sheets
   *  whose owner deliberately opens them right before navigating — the market
   *  lifecycle celebration opens on the proposal route and must still be on
   *  screen once the graduated market has loaded. Silk's page-scroll trap then
   *  stays attached across the navigation, so the new route keeps the offset
   *  the sheet was opened at until it closes. */
  persistAcrossRoutes?: boolean | undefined;
}
interface SilkSheetComponent {
  (props: SilkSheetProps): React.ReactNode;
  Trigger: ComponentType<{
    asChild?: boolean;
    children: ReactNode;
  }>;
  Close: ComponentType<{
    asChild?: boolean;
    children: ReactNode;
  }>;
  Body: ComponentType<{
    className?: string;
    children?: ReactNode;
  }>;
}

// ── Implementation ────────────────────────────────────────────────────────

interface SilkSheetModel {
  isDesktop: boolean;
  hasHeaderSlot: boolean;
  hasTitle: boolean;
  hasDescription: boolean;
  detents: readonly string[];
  contentPlacement: 'top' | 'bottom' | 'left' | 'right' | 'center';
  keyboardActive: boolean;
  keyboardOpen: boolean;
  widthCapClass: string;
  isDetachedOrPage: boolean;
  contentStyle: CSSProperties;
  collapseInFlowFootprint: boolean;
  turnkeyModalOpen: boolean;
}

/**
 * All of SilkSheet's variant/viewport/keyboard derivation — visual-viewport CSS
 * vars, desktop detection, detents, content placement, the keyboard recipe
 * flag, the inline content style (Silk cascade-layer overrides), and the
 * in-flow-footprint collapse. Kept as a hook in this file so the component body
 * stays a thin renderer. (`viewRef` + its `travelHandler` deliberately stay in
 * the component — routing a ref through the returned model would trip the React
 * Compiler refs rule, which is not file-exempt here.)
 */
function useSilkSheet(props) {
  const $ = _c(32);
  const {
    variant: t0,
    side: t1,
    size: t2,
    surface: t3,
    title,
    description,
    header,
    trigger,
    keyboard: t4
  } = props;
  const variant = t0 === undefined ? "detached" : t0;
  const side = t1 === undefined ? "right" : t1;
  const size = t2 === undefined ? "default" : t2;
  const surface = t3 === undefined ? "secondary" : t3;
  const keyboard = t4 === undefined ? false : t4;
  useVisualViewportCssVars();
  const keyboardOpen = useKeyboardOpen();
  const turnkeyModalOpen = useTurnkeyModalActive();
  const isDesktop = useIsDesktop();
  const hasHeaderSlot = header !== undefined && header !== null;
  const hasTitle = title !== undefined && title !== null && title !== "";
  const hasDescription = description !== undefined && description !== null && description !== "";
  let t5;
  if ($[0] !== side || $[1] !== size || $[2] !== variant) {
    t5 = variant === "side" && (side === "top" || side === "bottom") ? SIDE_HEIGHT_DETENTS_TB[size] : [];
    $[0] = side;
    $[1] = size;
    $[2] = variant;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const detents = t5;
  let t6;
  if ($[4] !== isDesktop || $[5] !== side || $[6] !== variant) {
    t6 = variant === "detached" || variant === "page" ? isDesktop ? "center" : "bottom" : placementForSide(side);
    $[4] = isDesktop;
    $[5] = side;
    $[6] = variant;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const contentPlacement = t6;
  const keyboardActive = (variant === "detached" && keyboard || variant === "page") && !isDesktop;
  const widthCapClass = variant === "detached" || variant === "page" ? DESKTOP_MAX_WIDTH[size] : "";
  const t7 = surface === "primary" ? "var(--background-primary)" : "var(--background-secondary)";
  let t8;
  if ($[8] !== t7) {
    t8 = {
      backgroundColor: t7
    };
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const baseContentStyle = t8;
  const isDetachedOrPage = variant === "detached" || variant === "page";
  const mobilePageHeight = variant === "page" && !isDesktop ? "90svh" : "auto";
  let t9;
  if ($[10] !== baseContentStyle || $[11] !== isDetachedOrPage || $[12] !== keyboardActive || $[13] !== mobilePageHeight || $[14] !== side || $[15] !== size || $[16] !== variant) {
    t9 = variant === "side" && (side === "left" || side === "right") ? {
      ...baseContentStyle,
      width: `min(100vw, ${SIDE_WIDTH_LR_CAP[size]})`
    } : isDetachedOrPage ? keyboardActive ? {
      ...baseContentStyle,
      height: mobilePageHeight,
      paddingBottom: "var(--keyboard-offset, 0px)"
    } : {
      ...baseContentStyle,
      height: mobilePageHeight
    } : baseContentStyle;
    $[10] = baseContentStyle;
    $[11] = isDetachedOrPage;
    $[12] = keyboardActive;
    $[13] = mobilePageHeight;
    $[14] = side;
    $[15] = size;
    $[16] = variant;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const contentStyle = t9;
  const collapseInFlowFootprint = trigger === undefined || trigger === null;
  let t10;
  if ($[18] !== collapseInFlowFootprint || $[19] !== contentPlacement || $[20] !== contentStyle || $[21] !== detents || $[22] !== hasDescription || $[23] !== hasHeaderSlot || $[24] !== hasTitle || $[25] !== isDesktop || $[26] !== isDetachedOrPage || $[27] !== keyboardActive || $[28] !== keyboardOpen || $[29] !== turnkeyModalOpen || $[30] !== widthCapClass) {
    t10 = {
      isDesktop,
      hasHeaderSlot,
      hasTitle,
      hasDescription,
      detents,
      contentPlacement,
      keyboardActive,
      keyboardOpen,
      widthCapClass,
      isDetachedOrPage,
      contentStyle,
      collapseInFlowFootprint,
      turnkeyModalOpen
    };
    $[18] = collapseInFlowFootprint;
    $[19] = contentPlacement;
    $[20] = contentStyle;
    $[21] = detents;
    $[22] = hasDescription;
    $[23] = hasHeaderSlot;
    $[24] = hasTitle;
    $[25] = isDesktop;
    $[26] = isDetachedOrPage;
    $[27] = keyboardActive;
    $[28] = keyboardOpen;
    $[29] = turnkeyModalOpen;
    $[30] = widthCapClass;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
interface SilkSheetInnerProps {
  /** The original SilkSheet props, re-destructured here for body rendering. */
  props: SilkSheetProps;
  hasHeaderSlot: boolean;
  hasTitle: boolean;
  hasDescription: boolean;
  keyboardActive: boolean;
  keyboardOpen: boolean;
}

/**
 * Sheet body — handle, header, scrollable content, footer, close button.
 * Split out of `SilkSheetImpl` to keep that component small (react-doctor
 * no-giant-component). Always rendered inside Sheet.Content's Silk context —
 * directly (non-float) or inside the mobile floating inner card. The absolute
 * close X anchors to whichever wrapper is `relative`.
 */
function SilkSheetInner(t0) {
  const $ = _c(33);
  const {
    props,
    hasHeaderSlot,
    hasTitle,
    hasDescription,
    keyboardActive,
    keyboardOpen
  } = t0;
  const {
    title,
    description,
    header,
    srOnlyHeader: t1,
    footer,
    children,
    closeButton: t2,
    contentClassName,
    showHandle: t3,
    guardDismiss: t4,
    onDismissAttempt,
    onOpenChange
  } = props;
  const srOnlyHeader = t1 === undefined ? false : t1;
  const closeButton = t2 === undefined ? true : t2;
  const showHandle = t3 === undefined ? false : t3;
  const guardDismiss = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== showHandle) {
    t5 = showHandle ? <div aria-hidden={true} className="mx-auto mt-3 mb-1 h-1.5 w-12 rounded-pill bg-foreground-tertiary/50" /> : null;
    $[0] = showHandle;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== description || $[3] !== hasDescription || $[4] !== hasHeaderSlot || $[5] !== hasTitle || $[6] !== header || $[7] !== keyboardActive || $[8] !== keyboardOpen || $[9] !== srOnlyHeader || $[10] !== title) {
    t6 = hasHeaderSlot ? <>{hasTitle ? <Sheet.Title className="sr-only">{title}</Sheet.Title> : null}{hasDescription ? <Sheet.Description className="sr-only">{description}</Sheet.Description> : null}<div className="px-4 pt-4 sm:px-6 sm:pt-6">{header}</div></> : srOnlyHeader ? <>{hasTitle ? <Sheet.Title className="sr-only">{title}</Sheet.Title> : null}{hasDescription ? <Sheet.Description className="sr-only">{description}</Sheet.Description> : null}</> : hasTitle || hasDescription ? <div className="flex flex-col gap-2 px-4 pt-4 sm:px-6 sm:pt-6">{hasTitle ? <Sheet.Title className="text-heading-md">{title}</Sheet.Title> : null}{hasDescription ? <Sheet.Description className={keyboardActive && keyboardOpen ? "sr-only" : "text-body text-foreground-secondary"}>{description}</Sheet.Description> : null}</div> : null;
    $[2] = description;
    $[3] = hasDescription;
    $[4] = hasHeaderSlot;
    $[5] = hasTitle;
    $[6] = header;
    $[7] = keyboardActive;
    $[8] = keyboardOpen;
    $[9] = srOnlyHeader;
    $[10] = title;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = closeButton && !hasHeaderSlot && srOnlyHeader && "pt-14 sm:pt-12";
  let t8;
  if ($[12] !== contentClassName || $[13] !== t7) {
    t8 = cn("min-w-0 px-4 py-4 sm:px-6 sm:py-6", t7, contentClassName);
    $[12] = contentClassName;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== children || $[16] !== t8) {
    t9 = <Scroll.Root asChild={true}><Scroll.View className="min-h-0 flex-1"><Scroll.Content className={t8}>{children}</Scroll.Content></Scroll.View></Scroll.Root>;
    $[15] = children;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== footer || $[19] !== props.footerClassName || $[20] !== props.variant) {
    t10 = footer !== undefined && footer !== null ? <div className={cn("border-t border-solid border-border-primary p-2", props.variant === "page" && "pb-[max(0.5rem,env(safe-area-inset-bottom))]", props.footerClassName)}>{footer}</div> : null;
    $[18] = footer;
    $[19] = props.footerClassName;
    $[20] = props.variant;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== closeButton || $[23] !== guardDismiss || $[24] !== onDismissAttempt || $[25] !== onOpenChange) {
    t11 = closeButton ? <AnimatedButton variant="tertiary" size="sm" prefix={Cancel01Icon} aria-label="Close" onClick={() => guardDismiss ? onDismissAttempt?.() : onOpenChange(false)} className="absolute top-3 right-3 sm:top-4 sm:right-4" /> : null;
    $[22] = closeButton;
    $[23] = guardDismiss;
    $[24] = onDismissAttempt;
    $[25] = onOpenChange;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t5 || $[30] !== t6 || $[31] !== t9) {
    t12 = <>{t5}{t6}{t9}{t10}{t11}</>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t5;
    $[30] = t6;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}

/**
 * A route change dismisses the sheet in the same commit that mounts the new
 * page. Silk's page-scroll trap (a window `scroll` listener inside Sheet.View
 * that re-scrolls to the offset captured when the sheet opened) stays attached
 * through the exit animation, while Next scrolls a new route to the top from
 * its layout phase. Navigating from an open sheet (search result, mobile menu
 * link) therefore landed on the new page at the OLD offset whenever the route
 * committed inside that exit window. Keying Sheet.Root on the pathname runs the
 * trap's cleanup in the mutation phase, before Next scrolls, so no scroll
 * effect of our own is needed. `openedOn` remembers the route the sheet was
 * presented on so the remounted instance mounts closed instead of replaying
 * its entrance; `dismiss` hands the close back to the owner exactly like a
 * Silk-driven dismissal.
 *
 * A `persistAcrossRoutes` sheet opts out entirely: it is neither re-keyed nor
 * dismissed, so a celebration opened right before a programmatic route
 * replace survives the navigation it was opened for.
 */
function useRouteChangeDismissal(open, dismiss, persistAcrossRoutes) {
  const $ = _c(7);
  const pathname = usePathname();
  const [openedOn, setOpenedOn] = useState(open ? pathname : null);
  if (open && openedOn === null) {
    setOpenedOn(pathname);
  }
  if (!open && openedOn !== null) {
    setOpenedOn(null);
  }
  const staleRoute = !persistAcrossRoutes && open && openedOn !== null && openedOn !== pathname;
  let t0;
  let t1;
  if ($[0] !== dismiss || $[1] !== staleRoute) {
    t0 = () => {
      if (staleRoute) {
        dismiss(false);
      }
    };
    t1 = [staleRoute, dismiss];
    $[0] = dismiss;
    $[1] = staleRoute;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useIsomorphicLayoutEffect(t0, t1);
  const t2 = persistAcrossRoutes ? undefined : pathname;
  let t3;
  if ($[4] !== staleRoute || $[5] !== t2) {
    t3 = {
      rootKey: t2,
      staleRoute
    };
    $[4] = staleRoute;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function SilkSheetImpl(props) {
  const $ = _c(88);
  const {
    open,
    onOpenChange,
    onAfterClose,
    variant: t0,
    side: t1,
    trigger,
    sheetClassName,
    disableDesktopSwipe: t2,
    guardDismiss: t3,
    onDismissAttempt,
    persistAcrossRoutes: t4
  } = props;
  const variant = t0 === undefined ? "detached" : t0;
  const side = t1 === undefined ? "right" : t1;
  const disableDesktopSwipe = t2 === undefined ? false : t2;
  const guardDismiss = t3 === undefined ? false : t3;
  const persistAcrossRoutes = t4 === undefined ? false : t4;
  const {
    isDesktop,
    hasHeaderSlot,
    hasTitle,
    hasDescription,
    detents,
    contentPlacement,
    keyboardActive,
    keyboardOpen,
    widthCapClass,
    isDetachedOrPage,
    contentStyle,
    collapseInFlowFootprint,
    turnkeyModalOpen
  } = useSilkSheet(props);
  const viewRef = useRef(null);
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = t6 => {
      const {
        progress
      } = t6;
      if (viewRef.current !== null && progress < 0.999) {
        viewRef.current.focus();
      }
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const travelHandler = t5;
  const unmountingRef = useRef(false);
  const presentedSessionRef = useRef(open);
  const onAfterCloseRef = useRef(onAfterClose);
  let t6;
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      unmountingRef.current = false;
      return () => {
        unmountingRef.current = true;
      };
    };
    t7 = [];
    $[1] = t6;
    $[2] = t7;
  } else {
    t6 = $[1];
    t7 = $[2];
  }
  useIsomorphicLayoutEffect(t6, t7);
  let t8;
  let t9;
  if ($[3] !== onAfterClose) {
    t8 = () => {
      onAfterCloseRef.current = onAfterClose;
    };
    t9 = [onAfterClose];
    $[3] = onAfterClose;
    $[4] = t8;
    $[5] = t9;
  } else {
    t8 = $[4];
    t9 = $[5];
  }
  useIsomorphicLayoutEffect(t8, t9);
  let t10;
  let t11;
  if ($[6] !== open) {
    t10 = () => {
      if (open) {
        presentedSessionRef.current = true;
      }
    };
    t11 = [open];
    $[6] = open;
    $[7] = t10;
    $[8] = t11;
  } else {
    t10 = $[7];
    t11 = $[8];
  }
  useIsomorphicLayoutEffect(t10, t11);
  let t12;
  let t13;
  if ($[9] !== open) {
    t12 = () => {
      if (open || !presentedSessionRef.current) {
        return;
      }
      const timeoutId = window.setTimeout(() => {
        if (unmountingRef.current || !presentedSessionRef.current) {
          return;
        }
        presentedSessionRef.current = false;
        onAfterCloseRef.current?.();
      }, DURATION_EXIT_MS);
      return () => window.clearTimeout(timeoutId);
    };
    t13 = [open];
    $[9] = open;
    $[10] = t12;
    $[11] = t13;
  } else {
    t12 = $[10];
    t13 = $[11];
  }
  useEffect(t12, t13);
  let t14;
  if ($[12] !== onOpenChange || $[13] !== open) {
    t14 = function handlePresentedChange(presented) {
      if (presented) {
        presentedSessionRef.current = true;
        onOpenChange(true);
        return;
      }
      if (unmountingRef.current) {
        return;
      }
      const completedPresentedSession = presentedSessionRef.current;
      presentedSessionRef.current = false;
      if (open) {
        onOpenChange(false);
      }
      if (completedPresentedSession) {
        onAfterCloseRef.current?.();
      }
    };
    $[12] = onOpenChange;
    $[13] = open;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const handlePresentedChange = t14;
  const {
    rootKey,
    staleRoute
  } = useRouteChangeDismissal(open, handlePresentedChange, persistAcrossRoutes);
  const isMobileDetachedFloat = variant === "detached" && !isDesktop;
  const sheetSurfaceBg = contentStyle.backgroundColor;
  const t15 = keyboardActive ? "calc(var(--keyboard-offset, 0px) + max(env(safe-area-inset-bottom, 0px), 0.5rem))" : "max(env(safe-area-inset-bottom, 0px), 0.5rem)";
  let t16;
  if ($[15] !== t15) {
    t16 = {
      height: "auto",
      backgroundColor: "transparent",
      paddingLeft: "0.5rem",
      paddingRight: "0.5rem",
      paddingBottom: t15
    };
    $[15] = t15;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  const floatContentStyle = t16;
  let t17;
  if ($[17] !== hasDescription || $[18] !== hasHeaderSlot || $[19] !== hasTitle || $[20] !== keyboardActive || $[21] !== keyboardOpen || $[22] !== props) {
    t17 = <SilkSheetInner props={props} hasHeaderSlot={hasHeaderSlot} hasTitle={hasTitle} hasDescription={hasDescription} keyboardActive={keyboardActive} keyboardOpen={keyboardOpen} />;
    $[17] = hasDescription;
    $[18] = hasHeaderSlot;
    $[19] = hasTitle;
    $[20] = keyboardActive;
    $[21] = keyboardOpen;
    $[22] = props;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  const sheetInner = t17;
  const t18 = collapseInFlowFootprint ? "hidden" : undefined;
  let t19;
  if ($[24] !== t18) {
    t19 = cn("min-h-0", t18);
    $[24] = t18;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  const t20 = open && !staleRoute;
  let t21;
  if ($[26] !== trigger) {
    t21 = trigger !== undefined && trigger !== null ? <Sheet.Trigger asChild={true}>{trigger}</Sheet.Trigger> : null;
    $[26] = trigger;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== props.returnFocusRef) {
    t22 = dismissFocusHandler(props.returnFocusRef);
    $[28] = props.returnFocusRef;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  let t24;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = cn("z-modal");
    t24 = {
      height: "var(--silk-100-lvh-dvh-pct)"
    };
    $[30] = t23;
    $[31] = t24;
  } else {
    t23 = $[30];
    t24 = $[31];
  }
  let t25;
  if ($[32] !== keyboardActive) {
    t25 = keyboardActive ? {
      swipeOvershoot: false as const,
      onTravel: travelHandler
    } : {};
    $[32] = keyboardActive;
    $[33] = t25;
  } else {
    t25 = $[33];
  }
  let t26;
  if ($[34] !== disableDesktopSwipe || $[35] !== guardDismiss || $[36] !== isDesktop) {
    t26 = disableDesktopSwipe && isDesktop || guardDismiss ? {
      swipeDismissal: false as const
    } : {};
    $[34] = disableDesktopSwipe;
    $[35] = guardDismiss;
    $[36] = isDesktop;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  if ($[38] !== detents) {
    t27 = detents.length > 0 ? {
      detents: [...detents]
    } : {};
    $[38] = detents;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  let t29;
  let t30;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      easing: EASE_DEFAULT_CSS,
      duration: DURATION_ENTRANCE_MS
    };
    t29 = {
      easing: EASE_EXIT_CSS,
      duration: DURATION_EXIT_MS
    };
    t30 = {
      easing: EASE_POSITION_CSS,
      duration: DURATION_POSITION_MS
    };
    $[40] = t28;
    $[41] = t29;
    $[42] = t30;
  } else {
    t28 = $[40];
    t29 = $[41];
    t30 = $[42];
  }
  let t31;
  if ($[43] !== guardDismiss || $[44] !== onDismissAttempt || $[45] !== turnkeyModalOpen) {
    t31 = guardDismiss ? e => {
      e.changeDefault({
        dismiss: false,
        stopOverlayPropagation: true
      });
      onDismissAttempt?.();
    } : {
      dismiss: !turnkeyModalOpen,
      stopOverlayPropagation: true
    };
    $[43] = guardDismiss;
    $[44] = onDismissAttempt;
    $[45] = turnkeyModalOpen;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] !== guardDismiss || $[48] !== onDismissAttempt || $[49] !== turnkeyModalOpen) {
    t32 = guardDismiss ? e_0 => {
      e_0.changeDefault({
        dismiss: false,
        stopOverlayPropagation: true,
        nativePreventDefault: true
      });
      onDismissAttempt?.();
    } : {
      dismiss: !turnkeyModalOpen,
      stopOverlayPropagation: true,
      nativePreventDefault: true
    };
    $[47] = guardDismiss;
    $[48] = onDismissAttempt;
    $[49] = turnkeyModalOpen;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] !== isMobileDetachedFloat) {
    t33 = <Sheet.Backdrop {...isMobileDetachedFloat ? {
      style: {
        backgroundColor: "transparent"
      } as CSSProperties
    } : {}} />;
    $[51] = isMobileDetachedFloat;
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  let t34;
  if ($[53] !== isDetachedOrPage || $[54] !== isMobileDetachedFloat || $[55] !== sheetClassName || $[56] !== side || $[57] !== variant || $[58] !== widthCapClass) {
    t34 = sheetContentClass({
      isMobileDetachedFloat,
      isDetachedOrPage,
      widthCapClass,
      variant,
      side,
      sheetClassName
    });
    $[53] = isDetachedOrPage;
    $[54] = isMobileDetachedFloat;
    $[55] = sheetClassName;
    $[56] = side;
    $[57] = variant;
    $[58] = widthCapClass;
    $[59] = t34;
  } else {
    t34 = $[59];
  }
  const t35 = isMobileDetachedFloat ? floatContentStyle : contentStyle;
  let t36;
  if ($[60] !== isMobileDetachedFloat || $[61] !== sheetClassName || $[62] !== sheetInner || $[63] !== sheetSurfaceBg) {
    t36 = isMobileDetachedFloat ? <div className={cn("relative flex min-h-0 w-full flex-col overflow-hidden rounded-overlay border border-solid border-border-primary", sheetClassName)} style={{
      backgroundColor: sheetSurfaceBg
    }}>{sheetInner}</div> : sheetInner;
    $[60] = isMobileDetachedFloat;
    $[61] = sheetClassName;
    $[62] = sheetInner;
    $[63] = sheetSurfaceBg;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] !== t34 || $[66] !== t35 || $[67] !== t36) {
    t37 = <Sheet.Content className={t34} style={t35}>{t36}</Sheet.Content>;
    $[65] = t34;
    $[66] = t35;
    $[67] = t36;
    $[68] = t37;
  } else {
    t37 = $[68];
  }
  let t38;
  if ($[69] !== contentPlacement || $[70] !== t22 || $[71] !== t25 || $[72] !== t26 || $[73] !== t27 || $[74] !== t31 || $[75] !== t32 || $[76] !== t33 || $[77] !== t37) {
    t38 = <Sheet.Portal><Sheet.View ref={viewRef} onDismissAutoFocus={t22} className={t23} style={t24} nativeEdgeSwipePrevention={true} contentPlacement={contentPlacement} {...t25} {...t26} {...t27} enteringAnimationSettings={t28} exitingAnimationSettings={t29} steppingAnimationSettings={t30} onClickOutside={t31} onEscapeKeyDown={t32}>{t33}{t37}</Sheet.View></Sheet.Portal>;
    $[69] = contentPlacement;
    $[70] = t22;
    $[71] = t25;
    $[72] = t26;
    $[73] = t27;
    $[74] = t31;
    $[75] = t32;
    $[76] = t33;
    $[77] = t37;
    $[78] = t38;
  } else {
    t38 = $[78];
  }
  let t39;
  if ($[79] !== handlePresentedChange || $[80] !== rootKey || $[81] !== t20 || $[82] !== t21 || $[83] !== t38) {
    t39 = <Sheet.Root key={rootKey} license={SILK_LICENSE} presented={t20} onPresentedChange={handlePresentedChange}>{t21}{t38}</Sheet.Root>;
    $[79] = handlePresentedChange;
    $[80] = rootKey;
    $[81] = t20;
    $[82] = t21;
    $[83] = t38;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  let t40;
  if ($[85] !== t19 || $[86] !== t39) {
    t40 = <div className={t19}>{t39}</div>;
    $[85] = t19;
    $[86] = t39;
    $[87] = t40;
  } else {
    t40 = $[87];
  }
  return t40;
}
function sheetContentClass({
  isMobileDetachedFloat,
  isDetachedOrPage,
  widthCapClass,
  variant,
  side,
  sheetClassName
}: {
  isMobileDetachedFloat: boolean;
  isDetachedOrPage: boolean;
  widthCapClass: string;
  variant: SilkSheetVariant;
  side: SilkSheetSide;
  sheetClassName: string | undefined;
}): string {
  return cn('flex flex-col outline-none', isMobileDetachedFloat ?
  // Floating card: Sheet.Content is the transparent padding
  // layer (side + safe-area-bottom gap); the inner card below
  // owns the visible surface. Capped at dvh so a tall card
  // scrolls internally.
  'max-h-dvh w-full' : cn(
  // `relative` so the absolute close X anchors to the sheet.
  // No `bg-*` class — background is the inline style (Silk's
  // :where() bg rule outranks Tailwind utilities; see
  // contentStyle comment). Cap detached/page at the dynamic
  // viewport height; the inner body scrolls past the cap
  // (Silk's Content is `overflow: visible !important`).
  'relative shadow-elevation-modal', isDetachedOrPage && 'max-h-dvh', isDetachedOrPage && 'w-full rounded-t-overlay border-t border-solid border-border-primary sm:rounded-overlay sm:border', isDetachedOrPage && widthCapClass, variant === 'side' && 'h-full', variant === 'side' && side === 'right' && 'border-l border-solid border-border-primary', variant === 'side' && side === 'left' && 'border-r border-solid border-border-primary', variant === 'side' && side === 'top' && 'border-b border-solid border-border-primary', variant === 'side' && side === 'bottom' && 'border-t border-solid border-border-primary', 'overflow-x-hidden', sheetClassName));
}
function dismissFocusHandler(target: RefObject<HTMLElement | null> | undefined): (event: SilkDismissAutoFocusEvent) => void {
  return (event: SilkDismissAutoFocusEvent): void => {
    if (!target) return;
    event.changeDefault({
      focus: false
    });
    target.current?.focus({
      preventScroll: true
    });
  };
}
function SilkSheetBody(t0) {
  const $ = _c(5);
  const {
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export const SilkSheet: SilkSheetComponent = Object.assign(SilkSheetImpl, {
  Trigger: Sheet.Trigger as ComponentType<{
    asChild?: boolean;
    children: ReactNode;
  }>,
  Close: ((props: {
    asChild?: boolean;
    children: ReactNode;
  }) => props.asChild === true ? <Sheet.Trigger action="dismiss" asChild={true}>
        {props.children}
      </Sheet.Trigger> : <Sheet.Trigger action="dismiss">{props.children}</Sheet.Trigger>) as ComponentType<{
    asChild?: boolean;
    children: ReactNode;
  }>,
  Body: SilkSheetBody
});

/** Registers a portalled control with the containing sheet's focus and inert ownership. */
export const SilkSheetExternalOverlay: typeof ExternalOverlay.Root = ExternalOverlay.Root;